import React, { useState } from 'react';
import { Dropdown } from 'antd';
import { GlobalOutlined, DownOutlined } from '@ant-design/icons';
import ReactCountryFlag from 'react-country-flag';
import styled from 'styled-components';
import i18n from '../../../../locale/i18n';

// Language options with flags
const languages = [
  { code: 'en', name: 'English', country: 'GB' },
  { code: 'de', name: 'Deutsch', country: 'DE' },
  { code: 'es', name: 'Español', country: 'ES' },
  { code: 'fr', name: 'Français', country: 'FR' },
  { code: 'it', name: 'Italiano', country: 'IT' },
  { code: 'pt', name: 'Português', country: 'PT' },
  { code: 'ru', name: 'Русский', country: 'RU' },
  { code: 'uk', name: 'Українська', country: 'UA' },
  { code: 'pl', name: 'Polski', country: 'PL' },
  { code: 'tr', name: 'Türkçe', country: 'TR' },
  { code: 'ja', name: '日本語', country: 'JP' },
  { code: 'ko', name: '한국어', country: 'KR' },
  { code: 'zh', name: '中文', country: 'CN' },
];

// Styled components
const SwitcherButton = styled.button<{ $variant?: 'light' | 'dark' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${props => props.$variant === 'dark' ? '#007bff' : '#ffffff'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$variant === 'dark' 
      ? 'rgba(0, 123, 255, 0.08)' 
      : 'rgba(255, 255, 255, 0.15)'};
  }
  
  .flag-icon {
    font-size: 16px;
    line-height: 1;
  }
  
  .lang-code {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
  }
  
  .arrow-icon {
    font-size: 10px;
    opacity: 0.7;
  }
`;

const DropdownMenu = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  padding: 8px;
  min-width: 180px;
  max-height: 320px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }
`;

const LanguageItem = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: ${props => props.$isActive ? '#f0f5ff' : 'transparent'};
  color: ${props => props.$isActive ? '#1677ff' : '#434343'};
  font-size: 14px;
  font-weight: ${props => props.$isActive ? '600' : '500'};
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  
  &:hover {
    background: ${props => props.$isActive ? '#f0f5ff' : '#fafafa'};
  }
  
  .flag {
    font-size: 18px;
    line-height: 1;
  }
  
  .name {
    flex: 1;
  }
  
  .check {
    color: #1677ff;
    font-size: 12px;
  }
`;

interface LanguageSwitcherProps {
  variant?: 'light' | 'dark';
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'light' }) => {
  const [open, setOpen] = useState(false);
  const currentLang = i18n.language || 'en';
  
  const currentLanguage = languages.find(l => l.code === currentLang) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setOpen(false);
  };

  const dropdownContent = (
    <DropdownMenu>
      {languages.map((lang) => (
        <LanguageItem
          key={lang.code}
          $isActive={currentLang === lang.code}
          onClick={() => handleLanguageChange(lang.code)}
        >
          <ReactCountryFlag
            className="flag"
            countryCode={lang.country}
            svg
            style={{ width: '20px', height: '15px' }}
          />
          <span className="name">{lang.name}</span>
          {currentLang === lang.code && <span className="check">✓</span>}
        </LanguageItem>
      ))}
    </DropdownMenu>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
    >
      <SwitcherButton $variant={variant}>
        <ReactCountryFlag
          className="flag-icon"
          countryCode={currentLanguage.country}
          svg
          style={{ width: '18px', height: '14px' }}
        />
        <span className="lang-code">{currentLang}</span>
        <DownOutlined className="arrow-icon" />
      </SwitcherButton>
    </Dropdown>
  );
};

export default LanguageSwitcher;

